import { useState } from 'react';
import { CarnetVivo } from '@/components/carnet/CarnetVivo';
import { GoogleSignIn } from '@/components/carnet/GoogleSignIn';
import { carnetApi, mensajeErrorCarnet } from '@/services/api/carnet.api';
import { useCarnetStore } from '@/store/carnetStore';

/**
 * Portal del empleado — `/mi-carnet`.
 *
 * Vive fuera de `AppLayout`: sin barra lateral, sin dashboard, sin módulos.
 * Quien entra aquí lo hace con su correo corporativo y solo ve su carnet.
 *
 * Pensada para el navegador de un celular sostenido con una mano: una sola
 * columna, el QR ocupando el centro y las acciones al alcance del pulgar.
 */
export function MiCarnetPage() {
  const { token, empleado, setSesion, cerrarSesion } = useCarnetStore();
  const [entrando, setEntrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuAbierto, setMenuAbierto] = useState(false);

  const entrarConGoogle = async (idToken: string) => {
    setEntrando(true);
    setError(null);
    try {
      const sesion = await carnetApi.loginGoogle(idToken);
      setSesion(sesion.access_token, sesion.empleado);
    } catch (err) {
      setError(mensajeErrorCarnet(err, 'No pudimos validar tu cuenta.'));
    } finally {
      setEntrando(false);
    }
  };

  const invalidarCodigos = async () => {
    try {
      await carnetApi.rotarSemilla();
      setMenuAbierto(false);
      window.location.reload();
    } catch (err) {
      setError(mensajeErrorCarnet(err, 'No pudimos invalidar los códigos.'));
    }
  };

  return (
    // El degradado radial da profundidad sin robarle contraste al QR, que es
    // lo único que la cámara de la portería tiene que distinguir.
    <div className="relative flex min-h-[100svh] flex-col overflow-hidden bg-[#0B1B36] text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,#1E4A8F_0%,transparent_60%)]"
      />

      {/* ── Encabezado ─────────────────────────────────────────── */}
      <header className="relative z-10 flex items-center justify-between px-5 pt-[max(1rem,env(safe-area-inset-top))]">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7FA6E8]">
            GSH
          </p>
          <h1 className="text-base font-semibold tracking-tight">Mi carnet</h1>
        </div>

        {token && empleado && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              aria-label="Opciones"
              aria-expanded={menuAbierto}
              className="flex h-11 w-11 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <circle cx="10" cy="4" r="1.8" />
                <circle cx="10" cy="10" r="1.8" />
                <circle cx="10" cy="16" r="1.8" />
              </svg>
            </button>

            {menuAbierto && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuAbierto(false)}
                  aria-hidden
                />
                <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-2xl bg-white text-slate-800 shadow-2xl">
                  <div className="border-b border-slate-100 px-4 py-3">
                    <p className="truncate text-xs text-slate-500">{empleado.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void invalidarCodigos()}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-slate-50"
                  >
                    Invalidar códigos anteriores
                    <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                      Si perdiste el teléfono donde tenías el carnet abierto.
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={cerrarSesion}
                    className="w-full border-t border-slate-100 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    Cerrar sesión
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      {/* ── Cuerpo ─────────────────────────────────────────────── */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-5 py-8">
        {token && empleado ? (
          <CarnetVivo empleado={empleado} />
        ) : (
          <div className="flex w-full max-w-sm flex-col items-center text-center">
            <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden>
                <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="#7FA6E8" strokeWidth="1.8" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="#7FA6E8" strokeWidth="1.8" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="#7FA6E8" strokeWidth="1.8" />
                <path d="M14 14h3v3h-3zM18 18h3v3h-3z" fill="#7FA6E8" />
              </svg>
            </div>

            <h2 className="text-2xl font-semibold tracking-tight">Tu carnet de acceso</h2>
            <p className="mt-3 max-w-[19rem] text-sm leading-relaxed text-white/60">
              Ingresa con tu correo corporativo y muestra el código en portería.
            </p>

            <div className="mt-9">
              <GoogleSignIn onCredencial={entrarConGoogle} deshabilitado={entrando} />
            </div>

            {entrando && (
              <p className="mt-4 text-sm text-white/60">Validando tu cuenta…</p>
            )}

            {error && (
              <p className="mt-6 max-w-[19rem] rounded-xl bg-red-500/15 px-4 py-3 text-sm leading-relaxed text-red-200 ring-1 ring-red-400/30">
                {error}
              </p>
            )}
          </div>
        )}
      </main>

      <footer className="relative z-10 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-center">
        <p className="text-[11px] text-white/30">
          ¿Problemas para ingresar? Escribe a Gestión Humana.
        </p>
      </footer>
    </div>
  );
}
